import { useEffect } from "react";
import {
  ClampToEdgeWrapping,
  LinearFilter,
  NearestFilter,
  NearestMipmapLinearFilter,
  NearestMipMapLinearFilter,
  RepeatWrapping,
  SRGBColorSpace,
  Texture,
  TextureLoader,
} from "three";
import useStore from "./useStore";
import { useShallow } from "zustand/react/shallow";
import { env_map, assets_prefix } from "./constants";

const ANIMATED_TEXTURE_ENTRIES = [
  {
    namePattern: "Red_Waves_{i}",
    extension: ".png",
    directory: "Red Waves",
    fileCount: 60,
  },
  {
    namePattern: "Blue Waves_{i}",
    extension: ".png",
    directory: "Blue Waves",
    fileCount: 60,
  },
  {
    namePattern: "Blue_Waves_2_{i}",
    extension: ".png",
    directory: "Blue Waves 2 Emitters",
    fileCount: 60,
  },
  {
    namePattern: "fuse_signal_{i}",
    extension: ".png",
    directory: "Fuse Signal",
    fileCount: 50,
  },
  {
    namePattern: "flicker_1_{i}",
    extension: ".jpg",
    directory: "Flicker 1",
    fileCount: 20,
  },
  {
    namePattern: "flicker_3_{i}",
    extension: ".jpg",
    directory: "Flicker 3",
    fileCount: 20,
  },
  {
    namePattern: "flicker_4_{i}",
    extension: ".jpg",
    directory: "Flicker 4",
    fileCount: 20,
  },
  {
    namePattern: "flicker_5_{i}",
    extension: ".jpg",
    directory: "Flicker 5",
    fileCount: 20,
  },
];

const ALPHA_MAPS = [
  "1 Substrate Zoomed",
  "1 Substrate",
  "2 Nwell Zoomed",
  "2 NWell",
  "4 Poly Zoomed",
  "4 Poly",
  "5 Licon Zoomed",
  "5 Licon",
  "6 Li1 Zoomed",
  "6 Li1",
  "7 Mcon Zoomed",
  "7 Mcon",
  "8 Met1 Zoomed",
  "8 Met1",
  "9 Via Zoomed",
  "9 Via",
  "10 Met2 Zoomed",
  "10 Met2",
  "12 Met3",
  "12 Met3 Zoomed",
  "13 Via3",
  "13 Via3 Zoomed",
  "14 Met4",
  "14 Met4 Zoomed",
  "16 Tap Zoomed",
  "16 Tap",
];

interface TextureData {
  name: string;
  fileName: string;
}

function generateTextureData(
  directory: string,
  count: number,
  namePattern: string,
  extension: string,
): TextureData[] {
  return Array.from({ length: count }, (_, i) => {
    const name = `${namePattern.replace("{i}", i.toString())}`;
    return {
      name,
      fileName: `${assets_prefix}/${directory}/` + name + extension,
    };
  });
}

const preloadTextures = (
  texturesData: TextureData[],
  setIsFinishedLoadingTextures: (setIsFinishedLoadingTextures: boolean) => void,
  setTexturesCache: (textures: Texture[]) => void,
): void => {
  const loader = new TextureLoader();
  const loadTexture = async (
    textureData: TextureData,
  ): Promise<Texture | undefined> => {
    const texture: Texture | undefined = await new Promise(
      (resolve, reject) => {
        loader
          .loadAsync(textureData.fileName)
          .then((texture) => resolve(texture))
          .catch((error) => {
            console.error(
              "Error loading texture " + textureData.fileName,
              error,
            );
            resolve(undefined);
          });
      },
    );
    if (texture) {
      texture.userData.name = textureData.name;

      texture.flipY = false;
      texture.magFilter = LinearFilter;

      texture.colorSpace = SRGBColorSpace;

      // Minor texture optimizations to test after image compression.
      // texture.generateMipmaps = false;
      // texture.anisotropy = 4;
      // texture.minFilter = LinearFilter;
      texture.needsUpdate = true;
    }
    return texture;
  };

  console.log("Fetching textures.");
  Promise.all(texturesData.map((textureData) => loadTexture(textureData)))
    .then((oTextures) => {
      // @ts-ignore
      const textures: Texture[] = oTextures.filter(
        (texture: Texture | undefined) => texture !== undefined,
      );
      console.log("fetching environment");
      fetch(assets_prefix + env_map)
        .then(() => {
          setTexturesCache(textures);
          setIsFinishedLoadingTextures(true);
        })
        .catch((error) => console.error("Error loading env map", error));
    })
    .catch((error) => {
      console.error("error loading textures", error);
    });
};

const ALPHA_MAPS_PREFIX = assets_prefix + "/Alpha Maps Inverted/";

const useAssetsLoader = () => {
  const [setIsFinishedLoadingTextures, setTexturesCache] = useStore(
    useShallow((state: any) => [
      state.setIsFinishedLoadingTextures,
      state.setTexturesCache,
    ]),
  );
  const loadAssets = () => {
    const animatedTextures: TextureData[] = ANIMATED_TEXTURE_ENTRIES.flatMap(
      (file) => {
        return generateTextureData(
          file.directory,
          file.fileCount,
          file.namePattern,
          file.extension,
        );
      },
    );

    const allTextures = [
      ...ALPHA_MAPS.map((am) => ({
        name: am,
        fileName: ALPHA_MAPS_PREFIX + am + ".jpg",
      })),
      ...animatedTextures,
    ];

    preloadTextures(
      allTextures,
      setIsFinishedLoadingTextures,
      setTexturesCache,
    );
  };
  useEffect(() => {
    loadAssets();
  }, []);
};

export default useAssetsLoader;
